import * as p from '@clack/prompts'
import { spinner } from '@core-engine/utils/spinner.js'
import { parseRailwayJson, runRailway } from '@next-adapter/init/railway/runner.js'
import type {
  RailwayAccount,
  RailwayAuthResult,
  RailwayRunner
} from '@next-adapter/init/railway/types.js'
import pc from 'picocolors'

const WHOAMI_TIMEOUT_MS = 30_000
const LOGIN_TIMEOUT_MS = 300_000

function isRailwayAccount(value: unknown): value is RailwayAccount {
  if (!value || typeof value !== 'object') return false
  const account = value as Record<string, unknown>
  return (
    typeof account.email === 'string' &&
    Array.isArray(account.workspaces) &&
    account.workspaces.every(
      (workspace) =>
        workspace !== null &&
        typeof workspace === 'object' &&
        typeof (workspace as Record<string, unknown>).id === 'string' &&
        typeof (workspace as Record<string, unknown>).name === 'string'
    )
  )
}

export async function checkRailwayAuth(
  runner: RailwayRunner,
  cwd: string,
  env?: NodeJS.ProcessEnv
): Promise<RailwayAuthResult> {
  const result = await runRailway(runner, ['whoami', '--json'], {
    cwd,
    mode: 'capture',
    timeoutMs: WHOAMI_TIMEOUT_MS,
    env
  })

  if (!result.success) {
    return { authed: false, reason: result.timedOut ? 'timeout' : 'not-authed' }
  }

  const account = parseRailwayJson<unknown>(result.stdout)
  return isRailwayAccount(account) ? { authed: true, account } : { authed: false, reason: 'failed' }
}

async function checkRailwayProjectToken(
  runner: RailwayRunner,
  cwd: string,
  env: NodeJS.ProcessEnv
): Promise<RailwayAuthResult> {
  const result = await runRailway(runner, ['status', '--json'], {
    cwd,
    mode: 'capture',
    timeoutMs: WHOAMI_TIMEOUT_MS,
    env
  })
  if (!result.success) {
    return { authed: false, reason: result.timedOut ? 'timeout' : 'invalid-token' }
  }

  const project = parseRailwayJson<unknown>(result.stdout)
  if (!project || typeof project !== 'object') return { authed: false, reason: 'failed' }
  const record = project as Record<string, unknown>
  return typeof record.id === 'string' && typeof record.name === 'string'
    ? { authed: true }
    : { authed: false, reason: 'failed' }
}

export async function ensureRailwayAuth(
  runner: RailwayRunner,
  cwd: string,
  options: {
    interactive: boolean
    env?: NodeJS.ProcessEnv
    quietIfAuthed?: boolean
  }
): Promise<RailwayAuthResult> {
  const env = options.env ?? process.env
  const checkSpinner = spinner()
  checkSpinner.start(
    runner.source === 'npx' ? 'Checking Railway, This may take a moment' : 'Checking Railway'
  )
  const projectTokenWasProvided = Boolean(env.RAILWAY_TOKEN)
  const existing = projectTokenWasProvided
    ? await checkRailwayProjectToken(runner, cwd, env)
    : await checkRailwayAuth(runner, cwd, env)

  if (existing.authed) {
    if (options.quietIfAuthed) {
      checkSpinner.clear()
    } else {
      checkSpinner.stop(
        existing.account?.email
          ? `Signed in to Railway as ${pc.cyan(existing.account.email)}`
          : 'Signed in to Railway'
      )
    }
    return existing
  }

  const tokenWasProvided = projectTokenWasProvided || Boolean(env.RAILWAY_API_TOKEN)
  if (tokenWasProvided) {
    checkSpinner.stop('Could not verify the Railway token')
    return {
      authed: false,
      reason: existing.reason === 'timeout' ? 'timeout' : 'invalid-token'
    }
  }

  if (!options.interactive) {
    checkSpinner.clear()
    return { authed: false, reason: existing.reason }
  }

  checkSpinner.clear()
  p.log.info(
    'Sign in to Railway to continue. Railway will open your browser or show a device code.'
  )
  const login = await runRailway(runner, ['login'], {
    cwd,
    mode: 'inherit',
    timeoutMs: LOGIN_TIMEOUT_MS,
    env
  })

  if (!login.success) {
    return { authed: false, reason: login.timedOut ? 'timeout' : 'cancelled' }
  }

  const verifySpinner = spinner()
  verifySpinner.start('Verifying Railway sign-in')
  const verified = await checkRailwayAuth(runner, cwd, env)
  if (verified.authed) {
    verifySpinner.stop(
      verified.account?.email
        ? `Signed in to Railway as ${pc.cyan(verified.account.email)}`
        : 'Signed in to Railway'
    )
    return verified
  }

  verifySpinner.stop('Could not verify your Railway sign-in')
  return { authed: false, reason: 'failed' }
}
